import { useMemo, useState } from "react";
import { Text, mergeStyleSets } from "@fluentui/react";
import { IGameEntry, ITeam, IPhase } from "./LiveScoreboard";
import ScoresheetModal from "./ScoresheetModal";
import BackToTop from "./BackToTop";

interface IBracketGridSectionProps {
    games: Record<string, IGameEntry>;
    phases: IPhase[];
    teamDictionary: Record<string, ITeam>;
}

interface ICellData {
    rowScore: number;
    colScore: number;
    round: string;
    isFinished: boolean;
    key?: string;
}

interface ITeamRecord {
    teamId: string;
    wins: number;
    losses: number;
}

const styles = mergeStyleSets({
    phaseSection: {
        marginTop: 24,
    },
    phaseHeader: {
        fontSize: 22,
        fontWeight: 600,
        marginTop: 25,
        marginBottom: 4,
        paddingBottom: 6,
        borderBottom: "2px solid #0078d4",
    },
    bracketHeader: {
        fontSize: 14,
        fontWeight: 600,
        marginBottom: 4,
        marginTop: 12,
    },
    tableWrapper: {
        overflowX: "auto" as const,
        marginBottom: 16,
    },
    table: {
        borderCollapse: "collapse" as const,
        border: "1px solid #999",
        fontSize: 13,
    },
    headerCell: {
        padding: "6px 8px",
        borderBottom: "2px solid #999",
        borderRight: "1px solid #999",
        fontWeight: 600,
        textAlign: "center" as const,
        backgroundColor: "#f3f2f1",
        whiteSpace: "nowrap" as const,
    },
    rowHeaderCell: {
        padding: "6px 8px",
        borderRight: "2px solid #999",
        borderBottom: "1px solid #999",
        fontWeight: 600,
        whiteSpace: "nowrap" as const,
        textAlign: "left" as const,
        backgroundColor: "#f3f2f1",
    },
    teamLogo: {
        width: 20,
        height: 20,
        objectFit: "contain" as const,
        verticalAlign: "middle" as const,
        marginRight: 6,
    },
    cell: {
        padding: "4px 8px",
        borderBottom: "1px solid #999",
        borderRight: "1px solid #999",
        textAlign: "center" as const,
        whiteSpace: "nowrap" as const,
        fontSize: 12,
        lineHeight: "1.3",
    },
    selfCell: {
        padding: "4px 8px",
        borderBottom: "1px solid #999",
        borderRight: "1px solid #999",
        textAlign: "center" as const,
        backgroundColor: "#e0e0e0",
        fontWeight: 600,
    },
    winCell: {
        backgroundColor: "#90ee90",
    },
    lossCell: {
        backgroundColor: "#f08080",
    },
    recordCell: {
        padding: "6px 8px",
        borderBottom: "1px solid #999",
        fontWeight: 600,
        textAlign: "center" as const,
        backgroundColor: "#f3f2f1",
        whiteSpace: "nowrap" as const,
    },
});

/**
 * For a given phase+bracket, collect all games (finished or not) between teams
 * in that bracket, including carry-over from earlier phases.
 */
function collectBracketGames(
    games: Record<string, IGameEntry>,
    phases: IPhase[],
    phaseName: string,
    bracketTeamIds: string[]
): Map<string, ICellData> {
    const sortedPhases = [...phases].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const targetPhase = sortedPhases.find((p) => p.name === phaseName);
    const targetOrder = targetPhase?.order ?? 0;

    // Map key: "rowTeamId|colTeamId"
    const cellMap = new Map<string, ICellData>();

    const teamSet = new Set(bracketTeamIds);

    for (const [key, game] of Object.entries(games)) {
        const teamIds = Object.keys(game.teams);

        if (teamIds.length !== 2) {
            continue;
        }

        const [tA, tB] = teamIds;
        if (!teamSet.has(tA) || !teamSet.has(tB)) continue;

        // Check if game belongs to this bracket directly
        let belongs = game.phase === phaseName && game.bracket ===
            (phases.find((p) => p.name === phaseName)?.brackets.find((b) =>
                b.teamIds.includes(tA) && b.teamIds.includes(tB))?.name ?? "");

        // ...or if it's from an earlier phase and should carry over
        // also don't carry over tiebreaker games to later phases
        if (!belongs && !game.tiebreaker) {
            const gamePhase = sortedPhases.find((p) => p.name === game.phase);
            const gameOrder = gamePhase?.order ?? 0;
            if (!gamePhase?.noCarryovers && gameOrder <= targetOrder) {
                // Both teams must be in this bracket
                if (teamSet.has(tA) && teamSet.has(tB)) {
                    belongs = true;
                }
            }
        }

        if (!belongs) {
            continue;
        }

        // just skip tiebreaker games for wins losses and the grid display
        if (game.tiebreaker) {
            continue;
        }

        const scoreA = game.teams[tA].score;
        const scoreB = game.teams[tB].score;

        // Row=tA, Col=tB: show tA's score - tB's score
        cellMap.set(`${tA}|${tB}`, {
            rowScore: scoreA,
            colScore: scoreB,
            round: game.round,
            isFinished: game.isFinished,
            key,
        });

        // Row=tB, Col=tA: show tB's score - tA's score
        cellMap.set(`${tB}|${tA}`, {
            rowScore: scoreB,
            colScore: scoreA,
            round: game.round,
            isFinished: game.isFinished,
            key,
        });
    }

    return cellMap;
}

function computeRecords(
    teamIds: string[],
    cellMap: Map<string, ICellData>
): Map<string, ITeamRecord> {
    const records = new Map<string, ITeamRecord>();
    for (const teamId of teamIds) {
        records.set(teamId, { teamId, wins: 0, losses: 0 });
    }

    for (const [key, cell] of cellMap.entries()) {
        const [rowTeam] = key.split("|");
        if (!cell.isFinished) continue;
        const record = records.get(rowTeam);
        if (!record) continue;
        if (cell.rowScore > cell.colScore) {
            record.wins++;
        }
    }

    // Losses: count from the other side
    for (const [key, cell] of cellMap.entries()) {
        const [, colTeam] = key.split("|");
        if (!cell.isFinished) continue;
        const record = records.get(colTeam);
        if (!record) continue;
        if (cell.rowScore > cell.colScore) {
            record.losses++;
        }
    }

    return records;
}

export default function BracketGridSection({ games, phases, teamDictionary }: IBracketGridSectionProps) {
    const sortedPhases = useMemo(
        () => [...phases].sort((a, b) => (b.order ?? 0) - (a.order ?? 0)),
        [phases]
    );

    const grids = useMemo(() => {
        const result: {
            phaseName: string;
            brackets: {
                bracketName: string;
                sortedTeamIds: string[];
                cellMap: Map<string, ICellData>;
                records: Map<string, ITeamRecord>;
            }[];
        }[] = [];

        for (const phase of sortedPhases) {
            const phaseBrackets: typeof result[0]["brackets"] = [];

            for (const bracket of phase.brackets) {
                const cellMap = collectBracketGames(games, phases, phase.name, bracket.teamIds);
                if (cellMap.size === 0) continue;

                const records = computeRecords(bracket.teamIds, cellMap);

                // Sort teams by wins desc, then by name
                const sortedTeamIds = [...bracket.teamIds].sort((a, b) => {
                    const ra = records.get(a);
                    const rb = records.get(b);
                    const winsA = ra?.wins ?? 0;
                    const winsB = rb?.wins ?? 0;
                    if (winsB !== winsA) return winsB - winsA;
                    const nameA = teamDictionary[a]?.name ?? a;
                    const nameB = teamDictionary[b]?.name ?? b;
                    return nameA.localeCompare(nameB);
                });

                phaseBrackets.push({
                    bracketName: bracket.name,
                    sortedTeamIds,
                    cellMap,
                    records,
                });
            }

            if (phaseBrackets.length > 0) {
                result.push({ phaseName: phase.name, brackets: phaseBrackets });
            }
        }

        return result;
    }, [games, phases, sortedPhases, teamDictionary]);

    const [scoresheetGameKey, setScoresheetGameKey] = useState<string | null>(null);

    if (grids.length === 0) return null;

    return (
        <>
            {grids.map(({ phaseName, brackets }) => (
                <div key={phaseName} className={styles.phaseSection}>
                    <Text id={`grid-${phaseName.toLowerCase().replace(/\s+/g, "-")}`} className={styles.phaseHeader} block>
                        {phaseName} Bracket Grid
                    </Text>
                    {brackets.map(({ bracketName, sortedTeamIds, cellMap, records }) => (
                        <div key={bracketName}>
                            <Text className={styles.bracketHeader} block>
                                {bracketName}
                            </Text>
                            <div className={styles.tableWrapper}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th className={styles.headerCell}>Team</th>
                                            {sortedTeamIds.map((colTeamId) => (
                                                <th key={colTeamId} className={styles.headerCell}>
                                                    {teamDictionary[colTeamId]?.name ?? colTeamId}
                                                </th>
                                            ))}
                                            <th className={styles.headerCell}>Record</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedTeamIds.map((rowTeamId) => {
                                            const record = records.get(rowTeamId);
                                            return (
                                                <tr key={rowTeamId}>
                                                    <td className={styles.rowHeaderCell}>
                                                        {teamDictionary[rowTeamId]?.logoUrl && (
                                                            <img
                                                                src={teamDictionary[rowTeamId].logoUrl}
                                                                alt=""
                                                                className={styles.teamLogo}
                                                            />
                                                        )}
                                                        {teamDictionary[rowTeamId]?.name ?? rowTeamId}
                                                    </td>
                                                    {sortedTeamIds.map((colTeamId) => {
                                                        if (rowTeamId === colTeamId) {
                                                            return (
                                                                <td key={colTeamId} className={styles.selfCell}>
                                                                    ---
                                                                </td>
                                                            );
                                                        }
                                                        const cell = cellMap.get(`${rowTeamId}|${colTeamId}`);
                                                        if (!cell) {
                                                            return <td key={colTeamId} className={styles.cell} />;
                                                        }

                                                        let bgClass = "";
                                                        if (cell.isFinished) {
                                                            bgClass = cell.rowScore > cell.colScore
                                                                ? styles.winCell
                                                                : styles.lossCell;
                                                        }

                                                        return (
                                                            <td
                                                                key={colTeamId}
                                                                className={`${styles.cell} ${bgClass}`}
                                                                style={cell.key ? { cursor: "pointer" } : {}}
                                                                onClick={cell.key ? () => setScoresheetGameKey(cell.key!) : undefined}
                                                            >
                                                                {cell.rowScore}-{cell.colScore}
                                                                <br />
                                                                {cell.round}
                                                            </td>
                                                        );
                                                    })}
                                                    <td className={styles.recordCell}>
                                                        {record ? `${record.wins} - ${record.losses}` : ""}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                    <BackToTop />
                </div>
            ))}
            {scoresheetGameKey && games[scoresheetGameKey] && (
                <ScoresheetModal
                    gameKey={scoresheetGameKey}
                    game={games[scoresheetGameKey]}
                    teamDictionary={teamDictionary}
                    onDismiss={() => setScoresheetGameKey(null)}
                />
            )}
        </>
    );
}
